import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { PartnerApiError } from '@/lib/api'
import { pushConfigQuery } from '@/lib/queries'
import { deviceSupport, disableDevice, enableDevice, syncDevice, testDevice, type DeviceState } from './device'

/* Spec PARTNER-002 D-10 (copia do admin-app, ADMIN-002 D-13) — situacao dos avisos neste aparelho, para o cartao "Avisos neste aparelho".
   unavailable: servidor sem chaves · unsupported: navegador sem push · needs-install: iPhone/iPad fora do app
   instalado · denied: bloqueado no navegador · off / on. */

export type PushDeviceStatus = 'loading' | 'unavailable' | DeviceState | 'unsupported' | 'needs-install'

/** Uma sincronizacao por carga da pagina (o cartao e o menu nao repetem o PUT). */
let syncOnce: Promise<DeviceState> | null = null
const sync = (publicKey: string) => (syncOnce ??= syncDevice(publicKey).catch(() => 'off' as const))
/** Sair: a proxima pessoa neste navegador comeca do zero. */
export const forgetPushSync = () => { syncOnce = null }

export function usePushDevice() {
    const config = useQuery(pushConfigQuery)
    const [support] = useState(deviceSupport)
    const [state, setState] = useState<DeviceState | null>(null)
    const publicKey = config.data?.enabled ? config.data.publicKey : null

    useEffect(() => {
        if (!publicKey || support !== 'supported') return
        let alive = true
        void sync(publicKey).then(s => alive && setState(s))
        return () => { alive = false }
    }, [publicKey, support])

    const enable = useMutation({
        mutationFn: () => enableDevice(publicKey!),
        onSuccess: (s) => {
            syncOnce = Promise.resolve(s)
            setState(s)
        },
    })
    const disable = useMutation({
        mutationFn: disableDevice,
        onSuccess: (s) => {
            syncOnce = Promise.resolve(s)
            setState(s)
        },
    })
    const test = useMutation({
        mutationFn: testDevice,
        onError: (error) => {
            // O servidor nao conhece mais este aparelho (cancelado pelo navegador): pede para ligar de novo
            if (error instanceof PartnerApiError && (error.code === 'PARTNER_PUSH_GONE' || error.code === 'PARTNER_PUSH_NOT_FOUND')) setState('off')
        },
    })

    const status: PushDeviceStatus = config.isPending ? 'loading'
        : !publicKey ? 'unavailable'
            : support !== 'supported' ? support
                : state ?? 'loading'

    return { status, configError: config.error, enable, disable, test }
}

/** Sincroniza este aparelho ao abrir a area com sessao (AppLayout), sem mostrar nada. */
export function usePushSync() {
    const config = useQuery(pushConfigQuery)
    const publicKey = config.data?.enabled ? config.data.publicKey : null
    useEffect(() => {
        if (publicKey && deviceSupport() === 'supported') void sync(publicKey)
    }, [publicKey])
}
